import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Lock } from 'lucide-react';
import { Reveal } from '@/components/Reveal';

const API = '/api';

export default function CaseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/cases/${slug}`)
      .then(r => {
        setCaseData(r.data);
        document.title = `${r.data.title} | NKP VENTURES Case Experience`;
      })
      .catch(() => navigate('/case-experience'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16" style={{ background: '#0D1F30' }}>
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!caseData) return null;

  return (
    <div>
      {/* ── Hero + Breadcrumb — Integrated, no white gap ── */}
      <section
        className="relative pt-28 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #08111C 0%, #0D1F30 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.015) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          {/* Inline breadcrumb */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-xs text-white/30 font-sans mb-10"
          >
            <Link to="/" className="hover:text-white/55 transition-colors duration-150">Home</Link>
            <span className="text-white/15">/</span>
            <Link to="/case-experience" className="hover:text-white/55 transition-colors duration-150">Case Experience</Link>
            <span className="text-white/15">/</span>
            <span className="text-white/50 line-clamp-1 max-w-xs">{caseData.title}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/case-experience"
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors duration-150"
            >
              <ArrowLeft className="w-4 h-4" /> All Case Experiences
            </Link>

            {/* Client identity */}
            <div className="flex items-start gap-5 mb-8">
              <div className="w-14 h-14 bg-[#0E5A57] ring-1 ring-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="font-serif font-bold text-white text-xl">{caseData.client_name?.[0]}</span>
              </div>
              <div>
                <p className="overline-label text-[#D4AF37] mb-2">{caseData.industry || 'Case Experience'} · {caseData.client_name}</p>
                <h1 className="font-serif text-3xl lg:text-4xl font-bold text-white leading-tight max-w-3xl">
                  {caseData.title}
                </h1>
              </div>
            </div>

            <p className="text-white/55 font-sans leading-relaxed max-w-2xl mb-8">{caseData.summary}</p>

            {/* Services tags */}
            {caseData.services && caseData.services.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {caseData.services.map(s => (
                  <span key={s} className="text-xs text-teal-300/80 bg-white/8 border border-white/10 px-3 py-1.5 rounded-full font-sans">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── Confidentiality banner ── */}
      <div className="py-4 border-b border-[#D4AF37]/20" style={{ background: '#FDF8EC' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center gap-3">
          <Lock className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
          <p className="text-xs text-[#6B7280] font-sans">
            This case experience is published with client consent and excludes all confidential operational details, metrics, and proprietary information.
          </p>
        </div>
      </div>

      {/* ── Content ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="prose-pkepl" dangerouslySetInnerHTML={{ __html: caseData.content }} />
          </Reveal>
        </div>
      </section>

      {/* ── Tags ── */}
      {caseData.tags && caseData.tags.length > 0 && (
        <section className="py-6 bg-white border-t border-[#E5E7EB]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-[#9CA3AF] font-sans font-medium">Tags:</span>
            {caseData.tags.map(tag => (
              <span key={tag} className="text-xs text-[#374151] bg-[#F7FAFC] border border-[#E5E7EB] px-3 py-1.5 rounded-full font-sans">
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-28 bg-[#153E5C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-400 mb-4">Start Your Partnership</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Ready to discuss your<br />operational needs?
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Every engagement begins with a conversation. No obligation, no pressure.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/case-experience"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  <ArrowLeft className="w-4 h-4" /> All Cases
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
